import { useMemo, useState } from "react";
import { Slider } from "@/components/ui/slider";
import { money } from "@/lib/api";

const MODES = { stocks: { label: "Stocks", rate: 7 }, property: { label: "Real estate", rate: 6 } };

const Row = ({ label, value, children }) => (
  <div className="space-y-3">
    <div className="flex items-center justify-between text-sm">
      <span className="text-slate-400">{label}</span>
      <span className="font-mono tracking-tight text-white">{value}</span>
    </div>
    {children}
  </div>
);

export default function YieldCalculator() {
  const [mode, setMode] = useState("stocks");
  const [initial, setInitial] = useState(25000);
  const [monthly, setMonthly] = useState(500);
  const [years, setYears] = useState(10);
  const rate = MODES[mode].rate;

  const future = useMemo(() => {
    const r = rate / 100 / 12;
    const n = years * 12;
    return initial * Math.pow(1 + r, n) + monthly * ((Math.pow(1 + r, n) - 1) / r);
  }, [initial, monthly, years, rate]);
  const contributed = initial + monthly * years * 12;

  return (
    <div className="panel grid gap-10 p-6 sm:p-10 lg:grid-cols-2" data-testid="yield-calculator">
      <div className="space-y-8">
        <div className="inline-flex rounded-full border border-line p-1">
          {Object.entries(MODES).map(([k, m]) => (
            <button key={k} onClick={() => setMode(k)} data-testid={`calc-mode-${k}`}
              className={`rounded-full px-5 py-2 text-sm transition-colors duration-200 ${mode === k ? (k === "stocks" ? "bg-emerald-500 text-obsidian" : "bg-amber-600 text-white") : "text-slate-400 hover:text-white"}`}>
              {m.label}
            </button>
          ))}
        </div>
        <Row label="Initial investment" value={money(initial, 0)}>
          <Slider value={[initial]} min={1000} max={250000} step={1000} onValueChange={([v]) => setInitial(v)} data-testid="calc-initial-slider" />
        </Row>
        <Row label="Monthly contribution" value={money(monthly, 0)}>
          <Slider value={[monthly]} min={0} max={5000} step={50} onValueChange={([v]) => setMonthly(v)} data-testid="calc-monthly-slider" />
        </Row>
        <Row label="Time horizon" value={`${years} years`}>
          <Slider value={[years]} min={1} max={30} step={1} onValueChange={([v]) => setYears(v)} data-testid="calc-years-slider" />
        </Row>
      </div>
      <div className="flex flex-col justify-between rounded-2xl border border-line bg-obsidian/60 p-8">
        <div>
          <p className="eyebrow">Projected value</p>
          <p className={`mt-3 font-mono text-4xl tracking-tighter sm:text-5xl ${mode === "stocks" ? "text-emerald-400" : "text-amber-500"}`} data-testid="calc-projected-value">
            {money(future, 0)}
          </p>
          <div className="mt-8 grid grid-cols-2 gap-6 text-sm">
            <div><p className="text-slate-500">You contribute</p><p className="mt-1 font-mono text-white">{money(contributed, 0)}</p></div>
            <div><p className="text-slate-500">Estimated growth</p><p className="mt-1 font-mono text-white">{money(future - contributed, 0)}</p></div>
          </div>
        </div>
        <p className="mt-8 text-xs leading-relaxed text-slate-500">
          Illustration only, assuming a constant {rate}% annual return compounded monthly. Actual returns vary and are not guaranteed.
        </p>
      </div>
    </div>
  );
}
